import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, HeartHandshake, HandHeart, Send, Leaf, X, ChevronLeft, ChevronRight, PartyPopper } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { PAGES } from "@/i18n/extra_translations";
import { PROGRAM_PAGES } from "@/data/programs";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const btnBase = "inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm tracking-[0.12em] uppercase font-semibold transition-colors duration-300";

const ProgramDetail = () => {
  const { slug } = useParams();
  const { t, lang } = useLanguage();
  const pg = PAGES[lang] || PAGES.en;
  const program = PROGRAM_PAGES[slug];
  const [lightbox, setLightbox] = useState(null);
  const [touchX, setTouchX] = useState(null);
  const [gallery, setGallery] = useState([]);

  useEffect(() => {
    if (!slug) return;
    setGallery(PROGRAM_PAGES[slug]?.gallery || []);
    axios.get(`${API}/program-gallery/${slug}`).then((r) => {
      if (Array.isArray(r.data.photos)) setGallery(r.data.photos);
    }).catch(() => {});
  }, [slug]);

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") setLightbox((i) => Math.min(i + 1, gallery.length - 1));
      if (e.key === "ArrowLeft") setLightbox((i) => Math.max(i - 1, 0));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, gallery.length]);

  if (!program) {
    return (
      <main className="min-h-screen flex items-center justify-center px-6 pt-16">
        <div className="text-center">
          <h1 className="font-display text-3xl text-[#1A2F24]">Program not found</h1>
          <Link to="/" data-testid="program-notfound-home" className="mt-6 inline-block text-sm text-[#F4A261] underline underline-offset-4">Back to home</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-28 pb-20 px-6" data-testid="program-detail-page">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="max-w-3xl mx-auto"
      >
        <div className="rounded-[2rem] overflow-hidden border border-[#E2E8E4] shadow-[0_30px_60px_-30px_rgba(30,53,42,0.3)] mb-10">
          <img src={program.image} alt={program.title} className={`w-full ${program.imageContain ? "h-80 sm:h-[30rem] object-contain bg-[#1A2F24]" : program.imageTop ? "h-64 sm:h-96 object-cover object-top" : "h-64 sm:h-96 object-cover"}`} data-testid="program-detail-image" />
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <h1 className="font-display font-light text-4xl sm:text-5xl tracking-tight text-[#1A2F24]" data-testid="program-detail-title">{program.title}</h1>
          {program.comingSoon && (
            <span data-testid="program-coming-soon" className="rounded-full bg-[#F4A261]/15 text-[#F4A261] px-4 py-1.5 text-xs tracking-[0.2em] uppercase font-semibold">{pg.coming_soon}</span>
          )}
        </div>

        <p className="mt-8 text-xs tracking-[0.3em] uppercase text-[#5C7368]">{pg.our_mission}</p>
        <p className="mt-3 text-base leading-relaxed text-[#5C7368]" data-testid="program-mission">{program.mission}</p>

        <h2 className="mt-12 font-display text-3xl text-[#1A2F24]">{pg.what_we_do}</h2>
        <ul className="mt-5 space-y-3" data-testid="program-what-we-do">
          {program.whatWeDo.map((item, i) => (
            <li key={i} className="flex items-start gap-3 text-base leading-relaxed text-[#5C7368]" data-testid={`program-bullet-${i}`}>
              <Leaf size={18} className="text-[#F4A261] mt-1 shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>

        <p className="mt-8 rounded-2xl bg-[#F2F4F0] border border-[#E2E8E4] px-6 py-4 text-sm leading-relaxed text-[#1E352A]" data-testid="program-free-note">{pg.free_note}</p>

        {gallery.length > 0 && (
          <div className="mt-14" data-testid="program-gallery">
            <h2 className="font-display text-3xl text-[#1A2F24]">{pg.gallery_title}</h2>
            <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="program-gallery-grid">
              {gallery.map((photo, i) => (
                <button key={i} onClick={() => setLightbox(i)} data-testid={`program-gallery-photo-${i}`}
                  className="rounded-2xl overflow-hidden border border-[#E2E8E4] bg-white/60 cursor-zoom-in group text-left">
                  <img src={photo.src} alt={photo.caption || `${program.title} photo ${i + 1}`} className="w-full h-44 sm:h-52 object-contain bg-[#F2F4F0] transition-transform duration-700 group-hover:scale-105" />
                  {photo.caption && <p className="px-4 py-3 text-xs leading-relaxed text-[#5C7368]" data-testid={`program-gallery-caption-${i}`}>{photo.caption}</p>}
                </button>
              ))}
            </div>
          </div>
        )}

        <a href="/#programs" data-testid="program-back-btn"
          className={`mt-10 border border-[#1E352A]/25 text-[#1E352A] hover:border-[#F4A261] hover:text-[#F4A261] ${btnBase}`}>
          <ArrowLeft size={15} /> {pg.back_programs}
        </a>
        <div className="mt-14 rounded-3xl bg-[#F2F4F0] border border-[#E2E8E4] p-8 flex flex-wrap gap-3 justify-center" data-testid="program-actions">
          <a href="/#donate" data-testid="program-donate-btn" className={`bg-[#F4A261] text-white hover:bg-[#1E352A] ${btnBase}`}>
            <HeartHandshake size={16} /> {t("nav_donate")}
          </a>
          <a href="/#volunteer" data-testid="program-volunteer-btn" className={`bg-[#1E352A] text-[#FAF9F6] hover:bg-[#F4A261] hover:text-white ${btnBase}`}>
            <HandHeart size={16} /> {t("nav_volunteer")}
          </a>
          <a href="/#contact" data-testid="program-contact-btn" className={`border border-[#1E352A]/25 text-[#1E352A] hover:border-[#F4A261] hover:text-[#F4A261] ${btnBase}`}>
            <Send size={15} /> {pg.contact_us}
          </a>
          {program.hostCta && (
            <a href="/#contact" data-testid="program-host-btn" className={`bg-[#C4D7D1] text-[#1E352A] hover:bg-[#F4A261] hover:text-white ${btnBase}`}>
              <PartyPopper size={16} /> {program.hostCta}
            </a>
          )}
        </div>
      </motion.div>

      <AnimatePresence>
        {lightbox !== null && gallery.length > 0 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-[#1A2F24]/95 flex items-center justify-center p-4"
            onClick={() => setLightbox(null)}
            onTouchStart={(e) => setTouchX(e.touches[0].clientX)}
            onTouchEnd={(e) => {
              if (touchX === null) return;
              const delta = e.changedTouches[0].clientX - touchX;
              if (delta < -50) setLightbox((i) => Math.min(i + 1, gallery.length - 1));
              if (delta > 50) setLightbox((i) => Math.max(i - 1, 0));
              setTouchX(null);
            }}
            data-testid="program-gallery-lightbox">
            <button onClick={() => setLightbox(null)} data-testid="program-lightbox-close" aria-label="Close"
              className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/10 flex items-center justify-center text-[#FAF9F6] hover:bg-[#F4A261] transition-colors duration-300"><X size={20} /></button>
            {lightbox > 0 && (
              <button onClick={(e) => { e.stopPropagation(); setLightbox(lightbox - 1); }} data-testid="program-lightbox-prev" aria-label="Previous photo"
                className="absolute left-3 sm:left-8 w-11 h-11 rounded-full bg-white/10 flex items-center justify-center text-[#FAF9F6] hover:bg-[#F4A261] transition-colors duration-300 z-10"><ChevronLeft size={22} /></button>
            )}
            {lightbox < gallery.length - 1 && (
              <button onClick={(e) => { e.stopPropagation(); setLightbox(lightbox + 1); }} data-testid="program-lightbox-next" aria-label="Next photo"
                className="absolute right-3 sm:right-8 w-11 h-11 rounded-full bg-white/10 flex items-center justify-center text-[#FAF9F6] hover:bg-[#F4A261] transition-colors duration-300 z-10"><ChevronRight size={22} /></button>
            )}
            <div className="flex flex-col items-center gap-4" onClick={(e) => e.stopPropagation()}>
              <img src={gallery[lightbox].src} alt={gallery[lightbox].caption || `${program.title} photo ${lightbox + 1}`}
                className="max-h-[78vh] max-w-full rounded-2xl object-contain" data-testid="program-lightbox-image" />
              {gallery[lightbox].caption && <p className="max-w-xl text-center text-sm leading-relaxed text-[#FAF9F6]" data-testid="program-lightbox-caption">{gallery[lightbox].caption}</p>}
            </div>
            <p className="absolute bottom-5 text-xs tracking-[0.2em] text-[#C4D7D1]">{lightbox + 1} / {gallery.length}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
};

export default ProgramDetail;
